import { Box, Typography } from "@mui/material";
import { useQuiz } from "@/state/useQuiz";
import { useQuizContext } from "@/state/QuizContext";
import { useQuizNavigation } from "@/state/useQuizNavigation";
import { useResultsStep } from "@/state/useResultsStep";
import { writeQuizStepToURL, writeResultsStepToURL } from "@/state/urlState";
import { StepId } from "@/domains/quiz/StepDefinitions";
import { isDogIncluded } from "@/helpers/dogInclusion";
import { GTM } from "@/services/gtm";
import { useCheckout } from "./useCheckout";
import { PawsLoader } from "@/components/shared/PawsLoader";
import { QUIZ } from "@/theme/quizTokens";
import {
  useExperimentValue,
  QUIZ_PRESENTATION_EXPERIMENT_KEY,
} from "@/experiments/useExperimentValue";

type ResultsFooterProps = {
  summaryBlocked?: boolean;
};

const TOTAL_STEPS = 6;

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function ResultsFooter({ summaryBlocked = false }: ResultsFooterProps) {
  const { dogs, activeDogId } = useQuiz();

  // Experiment lever (presentation only, default = current copy).
  const checkoutButtonText = useExperimentValue(
    QUIZ_PRESENTATION_EXPERIMENT_KEY,
    "checkout_button_text",
    "Go to Checkout"
  );
  const { state, dispatch } = useQuizContext();
  const { getDogStep } = useQuizNavigation();
  const { resultsStep, setResultsStep } = useResultsStep();

  const {
    submitting,
    dateError,
    checkoutError,
    goToCheckout,
    clearErrors,
    mealsUnderRecommended,
    usedPacks,
    recommendedPacks,
    hiddenPriceMap,
  } = useCheckout();

  const safeStep =
    resultsStep && resultsStep >= 1 && resultsStep <= TOTAL_STEPS
      ? resultsStep
      : 1;

  const isLast = safeStep === TOTAL_STEPS;
  const isMealStep = safeStep === 3;
  const isSupplementStep = safeStep === 4;
  const activeDog = dogs.find((dog) => dog.id === activeDogId) ?? dogs[0];
  // Single dog in the quiz -> use its actual name.
  // Multiple dogs -> generic phrase instead of picking one dog's name.
  const dogPossessive =
    dogs.length === 1
      ? `${activeDog?.form?.name || "your dog"}'s`
      : "your dogs'";

  const handleNext = async () => {
    if (isLast) {
      // Track checkout click (safe / non-blocking)
      try {
        // Issue #50: track from the first INCLUDED dog, matching what checkout builds.
        const dogId =
          state.dogOrder?.find((id) => isDogIncluded(state.dogForms?.[id])) ??
          state.dogOrder?.[0];
        if (dogId) {
          const form = (state.dogForms?.[dogId] ?? {}) as Record<string, unknown>;

          const totalPacks = Number(form.mealSelectedPacks ?? 0) || 0;
          const planDays = Number(form.selectedPlanDays ?? 0) || 0;
          const packSizeGrams = Number(form.selectedPackSize ?? 0) || 0;

          // hiddenPriceMap keys are your supported plans (7/14/28)
          const hiddenPriceForDays =
            hiddenPriceMap?.[planDays as 7 | 14 | 28] ?? null;

          const rawPricePerPack = Number(
            hiddenPriceForDays?.[packSizeGrams as 250 | 400 | 600 | 800] ?? 0
          );

          const totalValue = round2(rawPricePerPack * totalPacks);

          GTM.trackCheckoutButtonClicked({
            buttonLocation: "Results Footer",
            totalValue,
          });
        }
      } catch {
        // Don't block checkout for tracking errors
      }

      await goToCheckout();
      return;
    }

    clearErrors();
    const next = safeStep + 1;
    setResultsStep(next);
    writeResultsStepToURL(next);
  };

  const handleBackClick = () => {
    clearErrors();

    if (safeStep === 1) {
      const fallbackDogId = activeDogId ?? dogs[0]?.id;
      if (!fallbackDogId) return;

      const dogStep = getDogStep(fallbackDogId) as StepId;
      dispatch({ type: "SET_GLOBAL_STEP", step: dogStep });
      writeQuizStepToURL(dogStep, fallbackDogId);
      return;
    }

    const prev = safeStep - 1;
    setResultsStep(prev);
    writeResultsStepToURL(prev);
  };

  const isNextDisabled = submitting || (isMealStep && mealsUnderRecommended);
  const isSummaryBlocked = summaryBlocked && safeStep === 1;

  return (
    <>
      {/* Full-screen loading overlay during checkout */}
      {submitting && <PawsLoader action="checkout" fullScreen />}

      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          pb: "env(safe-area-inset-bottom)",
          background: QUIZ.color.blue,
          boxShadow: "0 -4px 17px rgba(0,0,0,0.14)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", md: 760 },
            mx: "auto",
            px: { xs: "24px", md: "32px" },
            py: { xs: "12px", md: "14px" },
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {/* Validation / error banners (above the buttons) */}
          {!isSummaryBlocked && isMealStep && mealsUnderRecommended && (
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: "0.8rem", md: "0.85rem" },
                color: QUIZ.color.brown,
                textAlign: "center",
                lineHeight: 1.25,
              }}
            >
              Please add at least{" "}
              <strong>
                {Math.max(recommendedPacks - usedPacks, 1)} more pot
                {recommendedPacks - usedPacks > 1 ? "s" : ""}
              </strong>{" "}
              to match the recommended plan.
            </Typography>
          )}

          {!isSummaryBlocked && (dateError || checkoutError) && (
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: "0.8rem", md: "0.85rem" },
                color: QUIZ.color.error,
                textAlign: "center",
                lineHeight: 1.3,
                fontWeight: 600,
                background: QUIZ.color.white,
                borderRadius: "8px",
                px: 2,
                py: 0.75,
              }}
            >
              {dateError || checkoutError}
            </Typography>
          )}

          {/* Button row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isSummaryBlocked ? "center" : "space-between",
              width: "100%",
            }}
          >
            <Box
              role="button"
              onClick={handleBackClick}
              sx={{
                fontFamily: QUIZ.font.body,
                fontWeight: 500,
                fontSize: { xs: "14.78px", md: "16px" },
                color: QUIZ.color.brown,
                cursor: "pointer",
                px: "12px",
                py: "6px",
                userSelect: "none",
                "&:hover": { opacity: 0.7 },
              }}
            >
              {safeStep === 1 ? "Back to Quiz" : "Previous"}
            </Box>

            {!isSummaryBlocked && (
              <Box
                role="button"
                aria-disabled={isNextDisabled}
                onClick={isNextDisabled ? undefined : handleNext}
                sx={{
                  bgcolor: QUIZ.color.brown,
                  color: QUIZ.color.white,
                  fontFamily: QUIZ.font.button,
                  fontWeight: 700,
                  fontSize: { xs: "16px", md: "18px" },
                  lineHeight: 1.5,
                  borderRadius: QUIZ.radius.pill,
                  px: { xs: "32px", md: "46px" },
                  py: "12px",
                  cursor: isNextDisabled ? "default" : "pointer",
                  opacity: isNextDisabled ? 0.55 : 1,
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  boxShadow: "0px 4px 3px rgba(0,0,0,0.1), 0px 2px 2px rgba(0,0,0,0.1)",
                  transition: "opacity 0.2s ease",
                  "&:hover": { opacity: isNextDisabled ? 0.55 : 0.9 },
                }}
              >
                {isLast
                  ? submitting
                    ? "Building your box..."
                    : checkoutButtonText
                  : isSupplementStep
                    ? `Pick ${dogPossessive} treats`
                    : "Next"}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}